import { useEffect, useState } from 'react';
import { ActionIcon, Box, Button, Center, Image, Loader } from '@mantine/core';
import { FaPlay, FaTimes, FaVideo } from 'react-icons/fa';
import defaultAvatar from '@assets/default-avatar.png';
import { usePersonalVideoUrl } from '@hooks/usePersonalVideoUrl';
import { useVideoDuration } from '@hooks/useVideoDuration';

interface ProfileAvatarWithVideoProps {
  profileImageUrl: string;
  personalVideoId: number | null;
  size?: number;
  fill?: boolean;
  onAddVideo?: () => void;
}

/**
 * The profile avatar. Renders either:
 *  - the video intro (poster + play overlay → inline playback) when one exists, or
 *  - the profile picture, with an owner-only "Add Your Video Intro!" CTA on the
 *    lower third when `onAddVideo` is provided.
 */
function ProfileAvatarWithVideo({
  profileImageUrl,
  personalVideoId,
  size = 322,
  fill = false,
  onAddVideo,
}: ProfileAvatarWithVideoProps) {
  const [playing, setPlaying] = useState(false);
  const { videoRef, ready, onLoadedMetadata, reset } =
    useVideoDuration(playing);

  // Prefetch URL and warm <video> under the poster before play.
  const { data, refetch, isLoading } = usePersonalVideoUrl(personalVideoId);
  const videoUrl = data?.url ?? null;

  const stopPlaying = () => {
    setPlaying(false);
    videoRef.current?.pause();
  };

  /**
   * Legacy MediaRecorder WebMs often report duration === Infinity and may never
   * fire `ended`. Once the file is fully loaded and the playhead is at the end
   * of the buffered range, treat that as natural completion.
   */
  const stopIfInfinityPlaybackFinished = () => {
    const video = videoRef.current;
    if (!video || Number.isFinite(video.duration)) return;
    if (video.networkState !== HTMLMediaElement.NETWORK_IDLE) return;
    if (video.buffered.length === 0) return;

    const bufferedEnd = video.buffered.end(video.buffered.length - 1);
    if (video.currentTime >= bufferedEnd - 0.25) {
      stopPlaying();
    }
  };

  useEffect(() => {
    setPlaying(false);
    reset();
  }, [personalVideoId, reset]);

  useEffect(() => {
    if (!playing || !ready) return;
    videoRef.current?.play()?.catch(() => {});
  }, [playing, ready, videoRef]);

  useEffect(() => {
    if (!playing) return undefined;

    const containsDialog = (node: Node): boolean =>
      node instanceof HTMLElement &&
      (node.matches('[role="dialog"]') ||
        node.querySelector('[role="dialog"]') !== null);

    const observer = new MutationObserver((mutations) => {
      const dialogOpened = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(containsDialog)
      );
      if (dialogOpened) videoRef.current?.pause();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing]);

  const squareStyle = fill
    ? {
        position: 'absolute' as const,
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }
    : {
        position: 'relative' as const,
        width: size,
        height: size,
        maxWidth: '100%',
        overflow: 'hidden',
      };

  const fillStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
    display: 'block',
  };

  if (personalVideoId) {
    const showVideo = playing && ready && !!videoUrl;
    const showLoading = playing && (!videoUrl || !ready || isLoading);

    return (
      <Box style={squareStyle} data-testid="profile-avatar-with-video">
        {videoUrl && (
          // eslint-disable-next-line jsx-a11y/media-has-caption -- profile intro, no captions
          <video
            key={videoUrl}
            ref={videoRef}
            src={videoUrl}
            controls={playing}
            autoPlay={playing && ready}
            preload="auto"
            onLoadedMetadata={onLoadedMetadata}
            onError={() => refetch()}
            onEnded={() => stopPlaying()}
            onWaiting={stopIfInfinityPlaybackFinished}
            onTimeUpdate={stopIfInfinityPlaybackFinished}
            style={{
              ...fillStyle,
              visibility: showVideo ? 'visible' : 'hidden',
              position: showVideo ? 'relative' : 'absolute',
              inset: showVideo ? undefined : 0,
            }}
          />
        )}

        {showVideo ? (
          <ActionIcon
            aria-label="Close video intro"
            radius="xl"
            size={32}
            onClick={() => stopPlaying()}
            pos="absolute"
            top={8}
            right={8}
            styles={{
              root: {
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: '#fff',
              },
            }}
          >
            <FaTimes size={16} />
          </ActionIcon>
        ) : (
          <>
            <Image
              src={profileImageUrl}
              fallbackSrc={defaultAvatar}
              alt="Video intro poster"
              style={fillStyle}
            />
            <Center pos="absolute" inset={0} style={{ pointerEvents: 'none' }}>
              {showLoading ? (
                <Loader
                  color="yellow"
                  role="status"
                  aria-label="Loading video"
                />
              ) : (
                <ActionIcon
                  aria-label="Play video intro"
                  radius="xl"
                  size={64}
                  onClick={() => setPlaying(true)}
                  style={{ pointerEvents: 'auto' }}
                  styles={{
                    root: {
                      backgroundColor: 'var(--yellow)',
                      color: '#000',
                    },
                  }}
                >
                  <FaPlay size={22} />
                </ActionIcon>
              )}
            </Center>
          </>
        )}
      </Box>
    );
  }

  return (
    <Box style={squareStyle} data-testid="profile-avatar-with-video">
      <Image
        src={profileImageUrl}
        fallbackSrc={defaultAvatar}
        alt="Profile"
        style={fillStyle}
      />
      {onAddVideo && (
        <Button
          leftSection={<FaVideo size={14} />}
          onClick={onAddVideo}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: '8%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          Add Your Video Intro!
        </Button>
      )}
    </Box>
  );
}

export default ProfileAvatarWithVideo;
